import { z } from "zod";
import { fallback } from "@tanstack/zod-adapter";
import {
  BookingRelocationMatchType,
  BookingStatus,
  RelocationType,
} from "@/gql/graphql";
import {
  BookingTableState,
  defaultBookingTableState,
} from "@/app/Bookings/Utils/bookingTableState";

const optionalSearchParam = <T extends z.ZodType>(schema: T) =>
  fallback(schema.optional(), undefined).default(undefined);

export const bookingTableSearchParams = z.object({
  bookingStatus: optionalSearchParam(z.array(z.enum(BookingStatus))),
  bookingSupplierId: optionalSearchParam(z.string()),
  bookingDepartureCityIds: optionalSearchParam(z.array(z.string())),
  bookingDeliveryCityIds: optionalSearchParam(z.array(z.string())),
  vehicleIds: optionalSearchParam(z.array(z.string())),
  relocationType: optionalSearchParam(z.array(z.enum(RelocationType))),
  hasMatches: optionalSearchParam(z.enum(BookingRelocationMatchType)),
  bookingPage: optionalSearchParam(z.number().int().positive()),
  bookingSearch: optionalSearchParam(z.string()),
});

export type BookingTableSearchParams = z.infer<typeof bookingTableSearchParams>;

export function parseBookingTableSearch(rawSearch: unknown) {
  return bookingTableSearchParams.parse(rawSearch);
}

export const bookingTableSearchKeys = Object.keys(
  bookingTableSearchParams.shape,
) as (keyof BookingTableSearchParams)[];

export function bookingTableStateFromSearch(
  search: BookingTableSearchParams,
): BookingTableState {
  return {
    statuses: search.bookingStatus ?? defaultBookingTableState.statuses,
    supplierId: search.bookingSupplierId ?? null,
    departureCityIds: search.bookingDepartureCityIds ?? [],
    deliveryCityIds: search.bookingDeliveryCityIds ?? [],
    vehicles: search.vehicleIds ?? [],
    relocationTypes: search.relocationType ?? [],
    hasMatches: search.hasMatches ?? null,
    page: search.bookingPage ?? 1,
    search: search.bookingSearch ?? "",
  };
}

export function bookingTableSearchFromState(
  state: BookingTableState,
): BookingTableSearchParams {
  return {
    bookingStatus: state.statuses.length ? state.statuses : undefined,
    bookingSupplierId: state.supplierId ?? undefined,
    bookingDepartureCityIds: state.departureCityIds.length
      ? state.departureCityIds
      : undefined,
    bookingDeliveryCityIds: state.deliveryCityIds.length
      ? state.deliveryCityIds
      : undefined,
    vehicleIds: state.vehicles.length ? state.vehicles : undefined,
    relocationType: state.relocationTypes.length
      ? state.relocationTypes
      : undefined,
    hasMatches: state.hasMatches ?? undefined,
    bookingPage: state.page > 1 ? state.page : undefined,
    bookingSearch: state.search || undefined,
  };
}
